// @ts-ignore
import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { useBooleanState } from 'react-use-object-state';
import Graph from 'react-vis-network-graph';
import { useGraphNetwork } from '../hooks/useGraphNetwork';
import {
  GraphViewType,
  NodePositionMap,
  SelectedNode,
  VisDataSet,
  VisEdge,
  VisNode,
} from './types';
import {
  getNodeCanvasPosition,
  getNodeDomPosition,
} from './utils/getNodePosition';
import * as config from './utils/graphViewConfig';
import handleDragWithRelatedNodes from './utils/handleDragWithRelatedNodes';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    root: {
      height: '100%',
      width: '100%',
      position: 'absolute',
      '&:focus': {
        outline: 'none',
      },
      '& div, & canvas': {
        '&:focus': {
          outline: 'none',
        },
      },
    },
  })
);

interface VisMouseEvent {
  event: { srcEvent: MouseEvent };
  nodes: any[];
  edges: any[];
}

interface NodeWithPosition {
  id: string;
  position: [number, number];
}

interface Props {
  selectedNodeId: string | null;
  nodes: VisNode[];
  edges: VisEdge[];
  onSelectEdge: (id: string | null) => void;
  onSelectNode: (node: SelectedNode) => void;
  onSelectedNodePosition: (x: number, y: number, nodeId?: string) => void;
  onUpdateNodePositions?: (nodes: NodeWithPosition[]) => void;
  graphType: GraphViewType;
  nodePositions?: NodePositionMap;
  moveAllConnectedNodes: boolean;
}

const GraphView = ({
  selectedNodeId,
  nodes,
  edges,
  onSelectEdge,
  onSelectNode,
  onSelectedNodePosition,
  onUpdateNodePositions,
  graphType = GraphViewType.mesh,
  moveAllConnectedNodes,
  nodePositions = {},
}: Props) => {
  const classes = useStyles();
  const [network, setNetwork] = useState<any>();
  const networkContextState = useGraphNetwork();
  const [graphNodes, setGraphNodes] = useState<VisDataSet>();
  const [animationTimeout, setAnimationTimeout] = useState<any>();
  const [forceRerenderCounter, setForceRerenderCounter] = useState<number>(0);
  const graphRef = useRef({} as any);
  const initialized = useBooleanState(false);
  const movedAllConnectedNodes = useBooleanState(false);

  const onGetNodes = (currentGraphNodes: VisDataSet) => {
    setGraphNodes(currentGraphNodes);
    Object.entries(nodePositions).forEach(([id, [x, y]]) => {
      // Only load positions for nodes that currently exist in the graph (will load default diamond node if you update a non-existent one)
      if (currentGraphNodes.get(id)) {
        currentGraphNodes.update({ id, fixed: true, x, y });
      }
    });
    setForceRerenderCounter(forceRerenderCounter + 1);
  };

  useEffect(() => {
    if (graphRef && graphRef.current) {
      onGetNodes((graphRef as any).current.nodes);
    }
  }, [nodes, edges, nodePositions, graphType, selectedNodeId]);

  useEffect(() => {
    if (network) {
      networkContextState[1](network);
    }
  }, [network]);

  const onGetNetwork = (newNetwork: any) => {
    setNetwork(newNetwork);
    newNetwork.once('resize', function () {
      setTimeout(() => newNetwork.fit());
    });
  };

  const onVisStabilizing = () => {
    // Force the animated node positions to stop after a little while.
    clearTimeout(animationTimeout);
    const newTimeout = setTimeout(() => {
      network.stopSimulation();
    }, config.MAX_ANIMATION_DURATION);
    setAnimationTimeout(newTimeout);
  };

  const click = (event: VisMouseEvent) => {
    const nodeId = event.nodes.length ? event.nodes[0] : null;
    if (nodeId) {
      const position = getNodeDomPosition(nodeId, network);
      onSelectNode({ id: nodeId, position: [position?.x, position?.y] });
      onSelectEdge(null);
    } else {
      const edgeId = event.edges.length ? event.edges[0] : null;
      if (edgeId) {
        onSelectNode({ id: null, position: [0, 0] });
        onSelectEdge(edgeId);
      } else {
        onSelectNode({ id: null, position: [0, 0] });
        onSelectEdge(null);
      }
    }
  };

  const updateSelectedNodePosition = () => {
    if (selectedNodeId) {
      const { x, y } = getNodeDomPosition(selectedNodeId, network);
      onSelectedNodePosition(x, y, selectedNodeId);
    }
  };

  const persistNodePositions = (nodeIds: string[]) => {
    if (onUpdateNodePositions) {
      const positionMapUpdate: NodeWithPosition[] = nodeIds.map((id) => {
        const { x, y } = getNodeCanvasPosition(id, network);
        return { id, position: [x, y] };
      });

      onUpdateNodePositions(positionMapUpdate);
    }
  };

  const dragEnd = (event: VisMouseEvent) => {
    const draggedNodeIds = event.nodes;
    draggedNodeIds.forEach((nodeId: string) => {
      graphNodes?.update({ id: nodeId, fixed: false });
    });
    const modifiedNodes = handleDragWithRelatedNodes({
      draggedNodeIds,
      graphType,
      isDragStart: false,
      edges,
      graphNodes,
      network,
    });

    updateSelectedNodePosition();
    if (draggedNodeIds.length) {
      persistNodePositions(
        movedAllConnectedNodes.state ? modifiedNodes : draggedNodeIds
      );
    }
  };

  const dragStart = (visEvent: VisMouseEvent) => {
    const draggedNodeIds = visEvent.nodes;
    const isMovingConnected =
      visEvent.event.srcEvent.shiftKey || moveAllConnectedNodes;
    movedAllConnectedNodes.setState(isMovingConnected);
    draggedNodeIds.forEach((nodeId: string) => {
      graphNodes?.update({ id: nodeId, fixed: false });
    });

    handleDragWithRelatedNodes({
      draggedNodeIds,
      graphType,
      isDragStart: true,
      isMovingConnected,
      edges,
      graphNodes,
      network,
    });
    // Hide the context menu when dragging
    onSelectedNodePosition(-1000, -1000, selectedNodeId as string);
  };

  const handleZoom = () => {
    updateSelectedNodePosition();
  };

  const handleStabilized = (data: any) => {
    if (!initialized.state) {
      network.stabilize();
      initialized.setTrue();
    }
  };

  const graphOptions = {
    autoResize: true,
    nodes: config.ORB_GRAPH_NODES,
    edges: config.CURVED_GRAPH_EDGES,
    physics: config.RIGID_GRAPH_PHYSICS,
    layout:
      graphType === GraphViewType.mesh
        ? config.MESH_LAYOUT
        : config.getTreeLayout(graphType),
    interaction: config.LIMITED_GRAPH_INTERACTION,
  };

  const graphEvents = {
    startStabilizing: onVisStabilizing,
    click: click,
    dragEnd: dragEnd,
    dragStart: dragStart,
    zoom: handleZoom,
    stabilized: handleStabilized,
  };

  return (
    <div className={classes.root}>
      <Graph
        ref={graphRef}
        graph={{ nodes, edges }}
        options={graphOptions}
        events={graphEvents}
        getNetwork={onGetNetwork}
        getNodes={onGetNodes}
      />
    </div>
  );
};

export default GraphView;
